import { useAtomValue, useSetAtom } from 'jotai';
import { selectors, ui } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { getAttachmentUrl, getBillPaymentBillAttachmentUrl } from 'modules/utils/helpers/getAttachmentUrl';
import { getRequest } from 'pages/requestForm/selectors/pageSelectors';
import { getActiveRequestSafe } from 'pages/requestList/selectors/pageSelectors';
import { getAllRequestAttachments } from 'pages/requestList/selectors/requestSelectors';
import { useCallback, useMemo } from 'react';
import { api } from 'services/api';
import {
    activeAttachmentIdState,
    attachmentModes,
    ContentSplitViewMode,
    ContentSplitViewState,
    contentSplitViewState,
    netSuiteBillPaymentApplyListState,
    netSuiteBillPaymentFieldValuesState,
    netSuiteExpenseLinesState,
} from 'shared/states';

import { useAuditReportStatus } from '../useAuditReportStatus';
import { getNetSuiteBillPaymentBillApplyListAttachments } from './useSplitViewContent.helpers';
import { messages } from './useSplitViewContent.messages';
import { UseGetActiveAttachmentParams } from './useSplitViewContent.types';

export const useSplitViewContent = () => {
    const contentSplitView = useAtomValue(contentSplitViewState);
    const activeAttachmentId = useAtomValue(activeAttachmentIdState);

    const request = useRequest(contentSplitView);

    useChangeModeForAttachmentsToUnknown(request, contentSplitView);

    const attachments = useAttachments(request, contentSplitView);

    const { goToPrevDocument, goToNextDocument } = useAttachmentsNavigation(attachments, activeAttachmentId);

    const auditReportStatus = useAuditReportStatus({
        mode: contentSplitView.mode,
        requestId: request?.id,
        companyId: request?.companyId,
    });

    const activeAttachment = useActiveAttachment({ activeAttachmentId, attachments, request, contentSplitView });

    const hasPaginator = attachmentModes.includes(contentSplitView.mode) && Boolean(activeAttachment);

    const attachmentIndex = useMemo(() => {
        if (hasPaginator) {
            return attachments.findIndex((attachment) => attachment.id === activeAttachmentId);
        }

        return 0;
    }, [activeAttachmentId, attachments, hasPaginator]);

    const canDownload =
        Boolean(activeAttachment) &&
        (attachmentModes.includes(contentSplitView.mode) ||
            (contentSplitView.mode === ContentSplitViewMode.AuditReport &&
                auditReportStatus === domain.RequestAuditReportStatus.Ready));

    const isVersionHistoryPreviewMode = contentSplitView.mode === ContentSplitViewMode.VersionHistory;

    const activeRequestVersion =
        (contentSplitView.mode === 'VersionHistory' && contentSplitView.activeRequestVersion) || undefined;

    const xeroBatchPaymentInvoiceName =
        (contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment &&
            contentSplitView.billFriendlyName) ||
        undefined;

    return {
        attachments,
        activeAttachment,
        auditReportStatus,
        goToNextDocument,
        goToPrevDocument,
        attachmentIndex,
        canDownload,
        hasPaginator,
        xeroBatchPaymentInvoiceName,
        activeRequestVersion,
        isVersionHistoryPreviewMode,
        contentSplitViewMode: contentSplitView.mode,
    };
};

const useRequest = (contentSplitView: ContentSplitViewState) => {
    const activePageId = useSelector(selectors.navigation.getActivePageId);

    const requestFormActiveRequest = useSelector(getRequest);
    const requestListActiveRequest = useSelector(getActiveRequestSafe);

    if (attachmentModes.includes(contentSplitView.mode) || contentSplitView.mode === ContentSplitViewMode.AuditReport) {
        return activePageId === 'requestForm' ? requestFormActiveRequest : requestListActiveRequest;
    }

    return null;
};

const useChangeModeForAttachmentsToUnknown = (
    request: ReturnType<typeof useRequest>,
    contentSplitView: ContentSplitViewState
) => {
    const setContentSplitView = useSetAtom(contentSplitViewState);

    const newAttachmentsMode = useMemo(() => {
        if (contentSplitView.mode === ContentSplitViewMode.AttachmentsToUnknown) {
            const attachmentsToRequest = request?.attachments || [];

            if (attachmentsToRequest.find((attachment) => attachment.id === contentSplitView.attachmentId))
                return ContentSplitViewMode.AttachmentsToRequest;

            const attachmentsToEmailToSupplier =
                (request?.details &&
                    (('emailToSupplier' in request.details && request.details.emailToSupplier?.attachments) ||
                        ('emailToContact' in request.details && request.details.emailToContact.attachments))) ||
                [];

            if (attachmentsToEmailToSupplier.find((attachment) => attachment.id === contentSplitView.attachmentId))
                return ContentSplitViewMode.AttachmentsToEmailToSupplier;

            const attachmentsToExpenseLineItem =
                request?.integrationCode === domain.IntegrationCode.NetSuiteExpenseReport
                    ? request.details.expenseLines.reduce<domain.RequestAttachment[]>((total, current) => {
                          if (current.attachment) {
                              total.push(current.attachment);
                          }

                          return total;
                      }, [])
                    : [];

            if (attachmentsToExpenseLineItem.find((attachment) => attachment.id === contentSplitView.attachmentId))
                return ContentSplitViewMode.AttachmentsToExpenseLineItem;

            const attachmentsToComments =
                request?.history.find(
                    (historyEvent) =>
                        historyEvent.comment &&
                        historyEvent.comment.attachments.some(
                            (attachment) => attachment.id === contentSplitView.attachmentId
                        )
                )?.comment?.attachments || [];

            if (attachmentsToComments.find((attachment) => attachment.id === contentSplitView.attachmentId))
                return ContentSplitViewMode.AttachmentsToComment;

            return null;
        }

        return null;
    }, [contentSplitView, request]);

    if (contentSplitView.mode === ContentSplitViewMode.AttachmentsToUnknown && newAttachmentsMode) {
        setContentSplitView({
            mode: newAttachmentsMode,
            attachmentId: contentSplitView.attachmentId,
        });
    }
};

const useAttachments = (request: ReturnType<typeof useRequest>, contentSplitView: ContentSplitViewState) => {
    const applyList = useAtomValue(netSuiteBillPaymentApplyListState);
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);

    return useSelector((state) => {
        switch (contentSplitView.mode) {
            case ContentSplitViewMode.AttachmentsToEmailToSupplier:
                return (
                    (request?.details &&
                        (('emailToSupplier' in request.details && request.details.emailToSupplier?.attachments) ||
                            ('emailToContact' in request.details && request.details.emailToContact.attachments))) ||
                    []
                );

            case ContentSplitViewMode.AttachmentsToRequest:
                return request?.attachments || [];

            case ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment:
                return (
                    (request &&
                        getAllRequestAttachments(state, request, {
                            xeroBatchPaymentInvoiceId: contentSplitView.billId,
                        })) ||
                    []
                );

            case ContentSplitViewMode.AttachmentsToNetSuiteBillLinkedToBillPayment:
                return getNetSuiteBillPaymentBillApplyListAttachments(request, applyList, contentSplitView.billId);

            case ContentSplitViewMode.AttachmentsToExpenseLineItem:
                return request?.integrationCode === domain.IntegrationCode.NetSuiteExpenseReport
                    ? (expenseLines.length ? expenseLines : request.details.expenseLines).reduce<
                          domain.RequestAttachment[]
                      >((total, current) => {
                          if (current.attachment) {
                              total.push(current.attachment);
                          }

                          return total;
                      }, [])
                    : [];

            case ContentSplitViewMode.AttachmentsToComment:
                return (
                    request?.history.find(
                        (historyEvent) =>
                            historyEvent.comment &&
                            historyEvent.comment.attachments.some(
                                (attachment) => attachment.id === contentSplitView.attachmentId
                            )
                    )?.comment?.attachments || []
                );

            default:
                return [];
        }
    });
};

const useActiveAttachment = ({
    activeAttachmentId,
    attachments,
    contentSplitView,
    request,
}: UseGetActiveAttachmentParams): ui.Document | null => {
    const billPaymentFieldValues = useAtomValue(netSuiteBillPaymentFieldValuesState);

    return useMemo(() => {
        if (!request) return null;

        switch (contentSplitView.mode) {
            case ContentSplitViewMode.AttachmentsToComment: {
                const attachment = attachments.find((a) => a.id === activeAttachmentId);

                if (!attachment) {
                    return null;
                }

                let url = api.requests.urls.getCommentAttachment({
                    attachmentId: attachment.id,
                    companyId: request.companyId,
                });

                // Hack to hide PDF name in PDF-viewer if the Title meta is empty.
                url = url.replace('getCommentAttachment?', 'getCommentAttachment/?');

                const fileType = selectors.request.expandRequestAttachment(attachment).fileType;

                return {
                    id: attachment.id,
                    name: attachment.name,
                    fileType,
                    url,
                };
            }

            case ContentSplitViewMode.AttachmentsToEmailToSupplier:
            case ContentSplitViewMode.AttachmentsToExpenseLineItem:
            case ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment:
            case ContentSplitViewMode.AttachmentsToNetSuiteBillLinkedToBillPayment:
            case ContentSplitViewMode.AttachmentsToRequest:
            case ContentSplitViewMode.AttachmentsToUnknown: {
                const activeAttachment = attachments.find((attachment) => attachment.id === activeAttachmentId);

                if (!activeAttachment) {
                    return null;
                }

                const fileType = selectors.request.expandRequestAttachment(activeAttachment).fileType;

                const url =
                    contentSplitView.mode === ContentSplitViewMode.AttachmentsToNetSuiteBillLinkedToBillPayment &&
                    request.integrationCode === domain.IntegrationCode.NetSuiteBillPayment
                        ? getBillPaymentBillAttachmentUrl({
                              attachmentId: activeAttachment.id,
                              companyId: request.companyId,
                              requestId: request.id,
                              billRequestId: contentSplitView.billId,
                              accountId:
                                  request.details.payablesAccount?.id ?? billPaymentFieldValues.payablesAccount?.id,
                              currencyId: request.details.currency?.id ?? billPaymentFieldValues.currency?.id,
                              payeeId: request.details.payee?.id ?? billPaymentFieldValues.payee?.id,
                              inline: true,
                          })
                        : getAttachmentUrl({
                              requestIntegrationCode: request.integrationCode,
                              attachmentId: activeAttachment.id,
                              companyId: request.companyId,
                              requestId: request.id,
                              billRequestId:
                                  contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment
                                      ? contentSplitView.billId
                                      : undefined,
                          });

                return {
                    id: activeAttachment.id,
                    name: activeAttachment.name,
                    fileType,
                    url,
                };
            }

            case ContentSplitViewMode.AuditReport: {
                return {
                    id: request.id,
                    name: messages.auditReportPreviewTitle({
                        requestDisplayName: request.displayName,
                    }),
                    fileType: selectors.types.FileType.Pdf,
                    url: api.requests.urls.getAuditReport(request.id, undefined, request.companyId),
                };
            }

            default:
                return null;
        }
    }, [activeAttachmentId, attachments, contentSplitView, request, billPaymentFieldValues]);
};

const useAttachmentsNavigation = (
    attachments: domain.RequestAttachment[],
    attachmentId?: domain.RequestAttachment['id']
) => {
    const setContentSplitView = useSetAtom(contentSplitViewState);

    const goToDocument = useCallback(
        (diff: number) => {
            const pageIndex = attachments.findIndex((attachment) => attachment.id === attachmentId);

            if (attachments.length <= 1) {
                return;
            }

            const maxPageIndex = attachments.length - 1;

            let newPageIndex = pageIndex + diff;

            if (newPageIndex < 0) {
                newPageIndex = 0;
            }

            if (newPageIndex > maxPageIndex) {
                newPageIndex = maxPageIndex;
            }

            if (pageIndex === newPageIndex) {
                return;
            }

            const newDocumentId = attachments[newPageIndex].id;

            setContentSplitView((contentSplitView) => ({
                ...contentSplitView,
                attachmentId: newDocumentId,
            }));
        },
        [attachmentId, attachments, setContentSplitView]
    );

    const goToNextDocument = useCallback(() => {
        goToDocument(1);
    }, [goToDocument]);

    const goToPrevDocument = useCallback(() => {
        goToDocument(-1);
    }, [goToDocument]);

    return { goToNextDocument, goToPrevDocument };
};
